import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { ContextMenu } from "radix-ui";
import { Icon } from "./Icon.tsx";
import { Image } from "./Image.tsx";
import { cx } from "./tokens.ts";
import type { MenuItem } from "./types.ts";

export type MediaGridItem = {
  key: string;
  /** Media pipeline source (file URLs, provider CDN URLs); animated WebP/GIF/APNG play. */
  src: string;
  /** Accessible name (sticker name, GIF title). */
  alt: string;
  width?: number;
  height?: number;
};

export type MediaGridSection = { key: string; title?: string; items: MediaGridItem[] };

/** Keys reported from a focused cell: arrows, Home/End. Enter/Space pick the cell (`onSelect`). */
export type MediaGridKey = "left" | "right" | "up" | "down" | "home" | "end";

export type MediaGridProps = {
  sections: MediaGridSection[];
  /** Cells per row (keyboard navigation uses the same number). */
  columns: number;
  /** "square": stickers (contain, transparent background); "tile": GIFs (cover, fixed row height). */
  cell?: "square" | "tile";
  /** The highlighted cell (keyboard), controlled. */
  activeKey?: string;
  onSelect: (key: string) => void;
  /** Arrow keys while a cell has the focus (the owner moves `activeKey`). */
  onKey?: (key: MediaGridKey) => void;
  /** Long-press / right-click actions of a cell. */
  contextMenu?: (key: string) => MenuItem[];
  /** The end of the grid scrolled into view (load more). */
  onEndReached?: () => void;
  /** Shown under the last row (spinner, error + retry, attribution). */
  footer?: ReactNode;
  testID: string;
};

const KEYS: Record<string, MediaGridKey> = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down", Home: "home", End: "end" };

/**
 * A scrolling grid of images in sections (sticker packs, GIF results): lazy-loaded images, a roving keyboard highlight
 * (arrows move it while a cell is focused; the owner can also move it from a search field), Enter picks, and
 * `onEndReached` for paging. Native: `LazyVGrid` / `LazyVerticalGrid` with animated decoders (primitive-gaps.md).
 */
export function MediaGrid(p: MediaGridProps) {
  const root = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const focusInside = useRef(false);
  const onEnd = useRef(p.onEndReached);
  useEffect(() => {
    onEnd.current = p.onEndReached;
  });

  // Keep the highlighted cell visible; move the focus to it when the keyboard is already in the grid.
  useEffect(() => {
    if (p.activeKey === undefined) return;
    const el = root.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(p.activeKey)}"]`);
    if (el === null || el === undefined) return;
    el.scrollIntoView({ block: "nearest" });
    if (focusInside.current) el.focus();
  }, [p.activeKey]);

  useEffect(() => {
    const el = sentinel.current;
    if (el === null) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onEnd.current?.();
    }, { root: root.current, rootMargin: "120px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const key = KEYS[e.key];
    if (key === undefined || p.onKey === undefined) return;
    e.preventDefault();
    p.onKey(key);
  };

  const square = (p.cell ?? "square") === "square";
  return (
    <div
      ref={root}
      className={cx("b-media-grid", square ? "b-media-grid-square" : "b-media-grid-tile")}
      style={{ ["--b-media-columns" as string]: String(Math.max(1, p.columns)) }}
      data-testid={p.testID}
      onKeyDown={onKeyDown}
      onFocus={() => {
        focusInside.current = true;
      }}
      onBlur={(e) => {
        if (!root.current?.contains(e.relatedTarget as Node | null)) focusInside.current = false;
      }}
      role="grid"
    >
      {p.sections.map((section) => (
        <div key={section.key} className="b-media-section" role="rowgroup" aria-label={section.title}>
          {section.title === undefined ? null : <div className="b-media-section-title">{section.title}</div>}
          <div className="b-media-cells">
            {section.items.map((item) => {
              const active = item.key === p.activeKey;
              const menu = p.contextMenu?.(item.key) ?? [];
              const cell = (
                <button
                  key={item.key}
                  type="button"
                  className={cx("b-media-cell", active && "b-media-cell-active")}
                  data-key={item.key}
                  data-testid={`${p.testID}-${item.key}`}
                  aria-label={item.alt}
                  title={item.alt}
                  tabIndex={active || (p.activeKey === undefined && item === p.sections[0]?.items[0]) ? 0 : -1}
                  onClick={() => p.onSelect(item.key)}
                >
                  <Image src={item.src} alt={item.alt} fit={square ? "contain" : "cover"} />
                </button>
              );
              if (menu.length === 0) return cell;
              return (
                <ContextMenu.Root key={item.key}>
                  <ContextMenu.Trigger asChild>{cell}</ContextMenu.Trigger>
                  <ContextMenu.Portal>
                    <ContextMenu.Content className="b-menu">
                      {menu.map((it) => (
                        <ContextMenu.Item key={it.id} className={cx("b-menu-item", it.destructive === true && "b-destructive")} onSelect={it.onSelect}>
                          {it.icon === undefined ? null : <Icon name={it.icon} size="icon.sm" />}
                          <span>{it.label}</span>
                        </ContextMenu.Item>
                      ))}
                    </ContextMenu.Content>
                  </ContextMenu.Portal>
                </ContextMenu.Root>
              );
            })}
          </div>
        </div>
      ))}
      {p.footer === undefined ? null : <div className="b-media-footer">{p.footer}</div>}
      <div ref={sentinel} className="b-media-sentinel" aria-hidden />
    </div>
  );
}
